import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Euro, Receipt, TrendingUp, Calendar, ShoppingBag, BarChart3, PieChart, ShoppingCart, Wine, UtensilsCrossed, Sparkles, X, ChevronRight, Search, AlertTriangle, CheckCircle, Coins, CreditCard, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, MONTH_NAMES } from '../utils/constants';
import { getApiHeaders } from '../utils/apiHelpers';

const AnalyticsPage = () => {
  const [kpis, setKpis] = useState(null);
  const [bottomItems, setBottomItems] = useState([]);
  const [trend, setTrend] = useState([]);
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Stato per importazione manuale email
  const [importingEmail, setImportingEmail] = useState(false);
  
  // Stato per ricerca articoli
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedSearchItem, setSelectedSearchItem] = useState(null);
  const searchDebounce = useRef(null);
  
  // Stato per classificazione AI
  const [topPiatti, setTopPiatti] = useState([]);
  const [topVini, setTopVini] = useState([]);
  const [aiClassificationLoading, setAiClassificationLoading] = useState(false);
  
  // Stato per il selettore periodo principale (KPIs)
  const [periodType, setPeriodType] = useState('day');
  const currentDate = new Date();
  const yesterday = new Date(currentDate);
  yesterday.setDate(yesterday.getDate() - 1);
  const [selectedYear, setSelectedYear] = useState(yesterday.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(yesterday.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(yesterday.getDate());
  // Date range per periodo personalizzato
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Ref per debounce
  const debounceTimer = useRef(null);
  
  // Stato per il selettore periodo AI (separato)
  const [aiPeriodType, setAiPeriodType] = useState('month');
  const [aiSelectedYear, setAiSelectedYear] = useState(yesterday.getFullYear());
  const [aiSelectedMonth, setAiSelectedMonth] = useState(yesterday.getMonth() + 1);
  const [aiSelectedDay, setAiSelectedDay] = useState(yesterday.getDate());
  
  // Stato per modal dettaglio prodotto
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [productDetailLoading, setProductDetailLoading] = useState(false);
  
  // Stato per modal dettaglio KPI giornaliero
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [kpiDailyData, setKpiDailyData] = useState(null);
  const [kpiDetailLoading, setKpiDetailLoading] = useState(false);
  
  // Stato per modal dettaglio categoria
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDetail, setCategoryDetail] = useState(null);
  const [categoryDetailLoading, setCategoryDetailLoading] = useState(false);
  
  // Stato per classificazione AI
  const [classifyingProducts, setClassifyingProducts] = useState(false);

  // Stato per breakdown finanziario (Contanti, POS, Spese)
  const [financialBreakdown, setFinancialBreakdown] = useState(null);
  const [expandedFinancial, setExpandedFinancial] = useState({ contanti: false, pos: false, spese: false });

  // Cache per i dati già caricati - evita ricaricamenti inutili
  const dataCache = useRef({});

  // Genera chiave cache basata sul periodo selezionato
  const getCacheKey = useCallback(() => {
    if (periodType === 'day') {
      return `day_${selectedYear}_${selectedMonth}_${selectedDay}`;
    } else if (periodType === 'month') {
      return `month_${selectedYear}_${selectedMonth}`;
    } else if (periodType === 'year') {
      return `year_${selectedYear}`;
    } else if (periodType === 'custom') {
      return `custom_${customStartDate}_${customEndDate}`;
    }
    return null;
  }, [periodType, selectedYear, selectedMonth, selectedDay, customStartDate, customEndDate]);

  useEffect(() => {
    // Non caricare se è personalizzato e mancano le date
    if (periodType === 'custom' && (!customStartDate || !customEndDate)) {
      setLoading(false);
      return;
    }
    
    // Controlla se i dati sono già in cache
    const cacheKey = getCacheKey();
    if (cacheKey && dataCache.current[cacheKey]) {
      // Usa i dati dalla cache - istantaneo!
      const cached = dataCache.current[cacheKey];
      setKpis(cached.kpis);
      setBottomItems(cached.bottomItems);
      setTrend(cached.trend);
      setCategories(cached.categories);
      setFinancialBreakdown(cached.financialBreakdown);
      setLoading(false);
      console.log('📊 AnalyticsPage: Dati caricati da cache per', cacheKey);
      return;
    }
    
    // Debounce: aspetta 300ms prima di caricare
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      loadAnalytics();
    }, 300);
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [periodType, selectedYear, selectedMonth, selectedDay, customStartDate, customEndDate, getCacheKey]);

  const loadAnalytics = async () => {
    // Skip se personalizzato senza date
    if (periodType === 'custom' && (!customStartDate || !customEndDate)) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setAiClassificationLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('📊 AnalyticsPage: Loading data, token exists:', !!token);
      if (!token) {
        console.error('📊 AnalyticsPage: No token found!');
        setLoading(false);
        return;
      }
      const headers = getApiHeaders();

      let startDate, endDate;
      if (periodType === 'day') {
        // Singolo giorno
        const dayStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
        startDate = dayStr;
        endDate = dayStr;
      } else if (periodType === 'month') {
        startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
        endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;
      } else if (periodType === 'year') {
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
      } else if (periodType === 'custom') {
        startDate = customStartDate;
        endDate = customEndDate;
      }

      // URL per KPIs - usa start_date/end_date per tutti i periodi
      let kpisUrl = `${API}/analytics/kpis?start_date=${startDate}&end_date=${endDate}`;

      // Date per bottom items - ultimi 12 mesi per catturare vendite recenti
      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const yearStartDate = oneYearAgo.toISOString().split('T')[0];
      const yearEndDate = today.toISOString().split('T')[0];

      // Carica dati base
      const [kpisRes, bottomItemsRes, trendRes, categoriesRes] = await Promise.all([
        axios.get(kpisUrl, { headers }),
        axios.get(`${API}/analytics/bottom-items?start_date=${yearStartDate}&end_date=${yearEndDate}&limit=10`, { headers }),
        axios.get(`${API}/analytics/trend?days=7`, { headers }),
        axios.get(`${API}/analytics/categories-integrated?start_date=${startDate}&end_date=${endDate}`, { headers })
      ]);

      const loadedKpis = kpisRes.data;
      const loadedBottomItems = bottomItemsRes.data.items || [];
      const loadedTrend = trendRes.data.trend || [];
      const loadedCategories = categoriesRes.data.categories || {};
      
      setKpis(loadedKpis);
      console.log('📊 AnalyticsPage: KPIs loaded:', loadedKpis);
      setBottomItems(loadedBottomItems);
      setTrend(loadedTrend);
      setCategories(loadedCategories);
      
      // Salva nella cache per evitare ricaricamenti futuri
      const cacheKey = getCacheKey();
      if (cacheKey) {
        dataCache.current[cacheKey] = {
          kpis: loadedKpis,
          bottomItems: loadedBottomItems,
          trend: loadedTrend,
          categories: loadedCategories,
          financialBreakdown: null, // Sarà popolato separatamente
          timestamp: Date.now()
        };
        console.log('📊 AnalyticsPage: Dati salvati in cache per', cacheKey);
      }
      
      // Termina il loading principale
      setLoading(false);
      setAiClassificationLoading(false);
      
      // NON caricare AI automaticamente - troppo lento
      // L'utente può cliccare "Classifica con AI" se vuole
      
    } catch (error) {
      console.error('📊 AnalyticsPage: Error loading analytics:', error);
      
      setLoading(false);
    }
  };

  // Funzione per caricare breakdown finanziario per periodo
  const loadFinancialBreakdown = async () => {
    // Controlla se i dati sono già in cache
    const cacheKey = getCacheKey();
    if (cacheKey && dataCache.current[cacheKey]?.financialBreakdown) {
      setFinancialBreakdown(dataCache.current[cacheKey].financialBreakdown);
      console.log('📊 AnalyticsPage: Financial breakdown caricato da cache per', cacheKey);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = getApiHeaders();
      
      // Calcola date in base al periodo
      let startDate, endDate;
      if (periodType === 'day') {
        startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
        endDate = startDate;
      } else if (periodType === 'month') {
        startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
        endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;
      } else if (periodType === 'year') {
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
      } else if (periodType === 'custom' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        return;
      }
      
      // Carica breakdown per ogni giorno del periodo e aggrega
      const response = await axios.get(
        `${API}/analytics/financial-breakdown?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      
      if (response.data.success) {
        const loadedBreakdown = response.data;
        setFinancialBreakdown(loadedBreakdown);
        
        // Salva nella cache
        if (cacheKey) {
          if (!dataCache.current[cacheKey]) {
            dataCache.current[cacheKey] = {};
          }
          dataCache.current[cacheKey].financialBreakdown = loadedBreakdown;
          console.log('📊 AnalyticsPage: Financial breakdown salvato in cache per', cacheKey);
        }
      }
    } catch (error) {
      console.error('Error loading financial breakdown:', error);
    }
  };

  // Carica breakdown quando cambia il periodo
  useEffect(() => {
    if (!loading && periodType !== 'custom' || (customStartDate && customEndDate)) {
      loadFinancialBreakdown();
    }
  }, [periodType, selectedYear, selectedMonth, selectedDay, customStartDate, customEndDate, loading]);

  // Funzione per cercare articoli
  const searchItem = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults(null);
      return;
    }
    
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = getApiHeaders();
      const res = await axios.get(`${API}/analytics/search-item?query=${encodeURIComponent(query)}`, { headers });
      setSearchResults(res.data);
    } catch (error) {
      console.error('Errore ricerca:', error);
      toast.error('Errore nella ricerca');
    } finally {
      setSearchLoading(false);
    }
  };

  // Funzione per importare manualmente i dati dalle email (in background)
  const handleImportEmail = async () => {
    setImportingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const headers = getApiHeaders();
      
      // Avvia sincronizzazione in background
      const res = await axios.post(`${API}/sync-emails`, {}, { headers });
      
      if (res.data.success) {
        toast.info('Sincronizzazione avviata in background...');
        
        // Polling per controllare lo stato
        const checkStatus = async () => {
          try {
            const statusRes = await axios.get(`${API}/sync-emails/status`, { headers });
            
            if (statusRes.data.is_running) {
              // Ancora in esecuzione - mostra progresso
              toast.loading(statusRes.data.progress || 'Elaborazione...', { id: 'email-sync' });
              setTimeout(checkStatus, 2000); // Ricontrolla tra 2 secondi
            } else {
              // Completato
              toast.dismiss('email-sync');
              setImportingEmail(false);
              
              if (statusRes.data.result?.success) {
                const { articoli_imported, finanziario_imported } = statusRes.data.result;
                if (articoli_imported > 0 || finanziario_imported > 0) {
                  toast.success(`Importati ${articoli_imported} report articoli e ${finanziario_imported} report finanziari`);
                  // Ricarica i dati analytics
                  dataCache.current = {}; // Pulisci cache
                  loadAnalytics();
                } else {
                  toast.info('Nessun nuovo dato da importare');
                }
              } else {
                toast.error(statusRes.data.result?.error || 'Errore durante l\'importazione');
              }
            }
          } catch (e) {
            console.error('Error checking status:', e);
            setImportingEmail(false);
            toast.dismiss('email-sync');
          }
        };
        
        // Inizia polling dopo 1 secondo
        setTimeout(checkStatus, 1000);
      } else {
        setImportingEmail(false);
        toast.error(res.data.error || 'Errore durante l\'avvio');
      }
    } catch (error) {
      console.error('Errore importazione email:', error);
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
      setImportingEmail(false);
    }
  };

  // Debounce per la ricerca
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchDebounce.current) {
      clearTimeout(searchDebounce.current);
    }
    
    searchDebounce.current = setTimeout(() => {
      searchItem(value);
    }, 400);
  };
  
  // Funzione separata per caricare AI senza bloccare
  const loadAiClassification = async (startDate, endDate, headers) => {
    try {
      const aiRes = await axios.get(
        `${API}/analytics/top-items-ai?start_date=${startDate}&end_date=${endDate}`, 
        { headers, timeout: 30000 }
      );
      if (aiRes.data.success) {
        setTopPiatti(aiRes.data.top_piatti || []);
        setTopVini(aiRes.data.top_vini || []);
      }
    } catch (aiError) {
      console.error('📊 AnalyticsPage: AI classification error:', aiError);
      // Fallback silenzioso
    } finally {
      setAiClassificationLoading(false);
    }
  };
  
  // Funzione per ricaricare solo la classificazione AI con filtro separato
  const reloadAiClassification = async () => {
    setAiClassificationLoading(true);
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = getApiHeaders();
    
    let startDate, endDate;
    if (aiPeriodType === 'day') {
      const dayStr = `${aiSelectedYear}-${String(aiSelectedMonth).padStart(2, '0')}-${String(aiSelectedDay).padStart(2, '0')}`;
      startDate = dayStr;
      endDate = dayStr;
    } else if (aiPeriodType === 'month') {
      startDate = `${aiSelectedYear}-${String(aiSelectedMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(aiSelectedYear, aiSelectedMonth, 0).getDate();
      endDate = `${aiSelectedYear}-${String(aiSelectedMonth).padStart(2, '0')}-${lastDay}`;
    } else {
      startDate = `${aiSelectedYear}-01-01`;
      endDate = `${aiSelectedYear}-12-31`;
    }
    
    await loadAiClassification(startDate, endDate, headers);
  };
  
  // Effetto per ricaricare AI quando cambiano i filtri AI
  useEffect(() => {
    if (!loading) {
      reloadAiClassification();
    }
  }, [aiPeriodType, aiSelectedYear, aiSelectedMonth, aiSelectedDay]);
  
  // Funzione per caricare dettaglio vendite prodotto
  const loadProductDetail = async (codice, descrizione) => {
    setSelectedProduct({ codice, descrizione });
    setProductDetailLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = getApiHeaders();
      
      // Usa lo stesso periodo del filtro AI
      let startDate, endDate;
      if (aiPeriodType === 'day') {
        const dayStr = `${aiSelectedYear}-${String(aiSelectedMonth).padStart(2, '0')}-${String(aiSelectedDay).padStart(2, '0')}`;
        startDate = dayStr;
        endDate = dayStr;
      } else if (aiPeriodType === 'month') {
        startDate = `${aiSelectedYear}-${String(aiSelectedMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(aiSelectedYear, aiSelectedMonth, 0).getDate();
        endDate = `${aiSelectedYear}-${String(aiSelectedMonth).padStart(2, '0')}-${lastDay}`;
      } else {
        startDate = `${aiSelectedYear}-01-01`;
        endDate = `${aiSelectedYear}-12-31`;
      }
      
      const response = await axios.get(
        `${API}/analytics/product-sales-detail?codice=${codice}&start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      
      if (response.data.success) {
        setProductDetail(response.data);
      }
    } catch (error) {
      console.error('Error loading product detail:', error);
      
    } finally {
      setProductDetailLoading(false);
    }
  };
  
  // Funzione per chiudere modal dettaglio
  const closeProductDetail = () => {
    setSelectedProduct(null);
    setProductDetail(null);
  };
  
  // Funzione per caricare dettaglio KPI giornaliero
  const loadKpiDailyBreakdown = async (metric, label, color) => {
    console.log('📊 Loading KPI breakdown for:', metric, label);
    setSelectedKpi({ metric, label, color });
    setKpiDetailLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = getApiHeaders();
      
      // Calcola date in base al periodo selezionato
      let startDate, endDate;
      if (periodType === 'month') {
        startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
        endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;
      } else {
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
      }
      
      console.log('📊 Fetching daily breakdown:', `${API}/analytics/daily-breakdown?start_date=${startDate}&end_date=${endDate}&metric=${metric}`);
      
      const response = await axios.get(
        `${API}/analytics/daily-breakdown?start_date=${startDate}&end_date=${endDate}&metric=${metric}`,
        { headers }
      );
      
      console.log('📊 KPI daily breakdown response:', response.data);
      
      if (response.data.success) {
        setKpiDailyData(response.data);
      }
    } catch (error) {
      console.error('Error loading KPI daily breakdown:', error);
      
    } finally {
      setKpiDetailLoading(false);
    }
  };
  
  // Funzione per chiudere modal KPI
  const closeKpiDetail = () => {
    setSelectedKpi(null);
    setKpiDailyData(null);
  };
  
  // Funzione per caricare dettaglio categoria
  const loadCategoryDetail = async (categoryName) => {
    console.log('📊 Loading category detail for:', categoryName);
    setSelectedCategory(categoryName);
    setCategoryDetailLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = getApiHeaders();
      
      let startDate, endDate;
      if (periodType === 'month') {
        startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
        endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;
      } else {
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
      }
      
      const url = `${API}/analytics/category-detail?category=${encodeURIComponent(categoryName)}&start_date=${startDate}&end_date=${endDate}`;
      console.log('📊 Fetching:', url);
      
      const response = await axios.get(url, { headers });
      
      console.log('📊 Category response:', response.data);
      
      if (response.data.success) {
        setCategoryDetail(response.data);
      }
    } catch (error) {
      console.error('Error loading category detail:', error);
      
    } finally {
      console.log('📊 Setting categoryDetailLoading to false');
      setCategoryDetailLoading(false);
    }
  };
  
  // Funzione per chiudere modal categoria
  const closeCategoryDetail = () => {
    setSelectedCategory(null);
    setCategoryDetail(null);
  };
  
  // Funzione per classificare prodotti con AI
  const classifyProductsWithAI = async () => {
    setClassifyingProducts(true);
    try {
      const response = await axios.post(
        `${API}/analytics/classify-products`,
        {},
        { headers: getApiHeaders() }
      );
      
      if (response.data.success) {
        toast.success(`${response.data.classified} prodotti classificati con AI!`);
        // Ricarica le categorie
        loadAnalytics();
      }
    } catch (error) {
      console.error('Error classifying products:', error);
      toast.error('Errore nella classificazione AI');
    } finally {
      setClassifyingProducts(false);
    }
  };
  
  // Calcola giorni nel mese per il selettore giorno
  const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

  const availableYears = [];
  for (let y = 2024; y <= currentDate.getFullYear(); y++) {
    availableYears.push(y);
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Selettore Periodo */}
      <div className="bg-slate-800/90 lg:bg-gradient-to-r lg:from-orange-50 lg:to-amber-50 rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-orange-200 p-4 lg:p-0">
      <div className="lg:pt-6">
          <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3 lg:gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-400 lg:text-orange-600" />
              <span className="font-medium text-slate-200 lg:text-gray-700">Periodo:</span>
            </div>
            
            <div className="flex bg-slate-700 lg:bg-white rounded-xl lg:rounded-lg p-1 shadow-sm lg:border overflow-x-auto">
              <button
                onClick={() => setPeriodType('day')}
                className={`px-3 py-2 rounded-lg lg:rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                  periodType === 'day' 
                    ? 'bg-orange-500 text-white shadow' 
                    : 'text-slate-300 lg:text-gray-600 hover:bg-slate-600 lg:hover:bg-gray-100'
                }`}
              >
                Giorno
              </button>
              <button
                onClick={() => setPeriodType('month')}
                className={`px-3 py-2 rounded-lg lg:rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                  periodType === 'month' 
                    ? 'bg-orange-500 text-white shadow' 
                    : 'text-slate-300 lg:text-gray-600 hover:bg-slate-600 lg:hover:bg-gray-100'
                }`}
              >
                Mese
              </button>
              <button
                onClick={() => setPeriodType('year')}
                className={`px-3 py-2 rounded-lg lg:rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                  periodType === 'year' 
                    ? 'bg-orange-500 text-white shadow' 
                    : 'text-slate-300 lg:text-gray-600 hover:bg-slate-600 lg:hover:bg-gray-100'
                }`}
              >
                Anno
              </button>
              <button
                onClick={() => setPeriodType('custom')}
                className={`px-3 py-2 rounded-lg lg:rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                  periodType === 'custom' 
                    ? 'bg-orange-500 text-white shadow' 
                    : 'text-slate-300 lg:text-gray-600 hover:bg-slate-600 lg:hover:bg-gray-100'
                }`}
              >
                Custom
              </button>
            </div>

            {/* Selettori in base al tipo di periodo */}
            {periodType === 'day' && (
              <div className="flex gap-2 flex-wrap">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
                >
                  {availableYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
                >
                  {MONTH_NAMES.slice(1).map((name, index) => (
                    <option key={index + 1} value={index + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                  className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
                >
                  {Array.from({length: getDaysInMonth(selectedYear, selectedMonth)}, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            )}

            {periodType === 'month' && (
              <div className="flex gap-2">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
                >
                  {availableYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
                >
                  {MONTH_NAMES.slice(1).map((name, index) => (
                    <option key={index + 1} value={index + 1}>{name}</option>
                  ))}
                </select>
              </div>
            )}

            {periodType === 'year' && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
              >
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}

            {periodType === 'custom' && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-400 lg:text-gray-600">Da:</span>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
                />
                <span className="text-sm text-slate-400 lg:text-gray-600">A:</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="border rounded-xl lg:rounded-lg px-3 py-2 text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 shadow-sm"
                />
              </div>
            )}

            <div className="flex flex-col lg:flex-row lg:ml-auto items-stretch lg:items-center gap-3 mt-2 lg:mt-0">
              {/* Pulsante Importa Dati */}
              <button
                onClick={handleImportEmail}
                disabled={importingEmail}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl lg:rounded-lg shadow-sm hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="Importa dati dalle email"
              >
                {importingEmail ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="text-sm font-medium">
                  {importingEmail ? 'Importazione...' : 'Importa Dati'}
                </span>
              </button>
              
              <div className="flex items-center gap-2 bg-slate-700 lg:bg-white px-4 py-2 rounded-xl lg:rounded-lg shadow-sm lg:border">
                <BarChart3 className="h-4 w-4 text-orange-400 lg:text-orange-600" />
                <span className="font-semibold text-white lg:text-gray-800 text-sm">
                  {periodType === 'day' 
                    ? `${selectedDay} ${MONTH_NAMES[selectedMonth]} ${selectedYear}`
                    : periodType === 'month' 
                      ? `${MONTH_NAMES[selectedMonth]} ${selectedYear}`
                      : periodType === 'year'
                        ? `Anno ${selectedYear}`
                        : customStartDate && customEndDate
                          ? `${customStartDate} - ${customEndDate}`
                          : 'Seleziona date'
                  }
                </span>
                {kpis?.days_with_data > 0 && (
                  <span className="text-xs text-slate-400 lg:text-gray-500 hidden lg:inline">
                    ({kpis.days_with_data} giorni)
                  </span>
                )}
              </div>
            </div>
          </div>
      </div>
      </div>

      {/* Barra Ricerca Articoli */}
      <div className="bg-slate-800/90 lg:bg-gradient-to-r lg:from-blue-50 lg:to-blue-50 rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-blue-200 p-4">
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Search className="h-5 w-5 text-blue-400 lg:text-blue-600" />
              <span className="font-medium text-slate-200 lg:text-gray-700">Cerca Articolo</span>
              <span className="text-sm text-slate-400 lg:text-gray-500 hidden sm:inline">- Storico vendite</span>
            </div>
            
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Cerca articolo..."
                className="w-full px-4 py-3 pl-12 border rounded-xl lg:rounded-lg text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900 placeholder-slate-400 lg:placeholder-gray-400 border-slate-600 lg:border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 lg:text-gray-400" />
              {searchLoading && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400 lg:border-blue-600"></div>
                </div>
              )}
            </div>

            {/* Risultati ricerca */}
            {searchResults && searchResults.items && searchResults.items.length > 0 && (
              <div className="bg-white rounded-lg border shadow-sm max-h-96 overflow-y-auto">
                {searchResults.items.map((item, index) => (
                  <div 
                    key={index}
                    className={`p-4 border-b last:border-b-0 cursor-pointer hover:bg-blue-50 transition-colors ${
                      selectedSearchItem?.codice === item.codice ? 'bg-blue-100' : ''
                    }`}
                    onClick={() => setSelectedSearchItem(selectedSearchItem?.codice === item.codice ? null : item)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{item.descrizione}</p>
                        <p className="text-xs text-gray-500">Codice: {item.codice}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{item.totale_quantita} pz venduti</p>
                        <p className="text-sm text-green-600">€{item.totale_importo?.toFixed(2)} totale</p>
                      </div>
                    </div>
                    
                    {/* Confronto prezzi */}
                    <div className="mt-3 flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Prezzo medio vendita:</span>
                        <span className="font-semibold">€{item.prezzo_medio_vendita?.toFixed(2)}</span>
                      </div>
                      {item.prezzo_menu !== null && (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Prezzo menu:</span>
                            <span className="font-semibold">€{item.prezzo_menu?.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {item.differenza_prezzo > 0.5 ? (
                              <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="h-4 w-4" />
                                +€{item.differenza_prezzo?.toFixed(2)} sopra menu
                              </span>
                            ) : item.differenza_prezzo < -0.5 ? (
                              <span className="flex items-center gap-1 text-red-600">
                                <AlertTriangle className="h-4 w-4" />
                                €{Math.abs(item.differenza_prezzo)?.toFixed(2)} sotto menu
                              </span>
                            ) : (
                              <span className="text-gray-500">= Prezzo allineato</span>
                            )}
                          </div>
                        </>
                      )}
                      {item.prezzo_menu === null && (
                        <span className="text-gray-400 italic">Prezzo menu non trovato</span>
                      )}
                    </div>

                    {/* Dettaglio vendite espandibile */}
                    {selectedSearchItem?.codice === item.codice && item.vendite && item.vendite.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="font-medium text-gray-700 mb-2">Storico vendite:</p>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {item.vendite.map((vendita, vIndex) => (
                            <div key={vIndex} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                              <span className="text-gray-600">{vendita.data}</span>
                              <span className="font-medium">{vendita.quantita} pz</span>
                              <span className="text-gray-500">€{vendita.prezzo_unitario?.toFixed(2)}/pz</span>
                              <span className="text-green-600 font-medium">€{vendita.importo_totale?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {searchResults && searchResults.items && searchResults.items.length === 0 && searchQuery.length >= 2 && (
              <div className="text-center py-4 text-slate-400 lg:text-gray-500">
                <Search className="h-8 w-8 mx-auto mb-2 text-slate-500 lg:text-gray-300" />
                <p>Nessun articolo trovato per &quot;{searchQuery}&quot;</p>
              </div>
            )}
          </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      ) : (
        <>
          {/* KPI Cards - Mobile Dark Theme */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <div 
              className="bg-slate-800/90 lg:bg-white border border-green-500/30 lg:border-l-4 lg:border-l-green-500 lg:border-t-0 lg:border-r-0 lg:border-b-0 rounded-2xl lg:rounded-xl p-4 cursor-pointer hover:bg-slate-700/90 lg:hover:shadow-lg transition-all"
              onClick={() => loadKpiDailyBreakdown('revenue', 'Incasso Totale', 'green')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-400 lg:text-gray-600 mb-1">Incasso Totale</p>
                  <p className="text-xl lg:text-2xl font-bold text-green-400 lg:text-green-600">
                    €{kpis?.total_revenue?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                  {kpis?.days_with_data > 1 && (
                    <p className="text-[10px] lg:text-xs text-slate-500 lg:text-gray-500 mt-1">
                      Media: €{kpis?.avg_daily_revenue?.toLocaleString('it-IT', {minimumFractionDigits: 2})}/g
                    </p>
                  )}
                </div>
                <div className="bg-green-500/20 lg:bg-green-100 p-2 lg:p-3 rounded-xl lg:rounded-full">
                  <Euro className="h-5 w-5 lg:h-6 lg:w-6 text-green-400 lg:text-green-600" />
                </div>
              </div>
            </div>

            <div 
              className="bg-slate-800/90 lg:bg-white border border-blue-500/30 lg:border-l-4 lg:border-l-blue-500 lg:border-t-0 lg:border-r-0 lg:border-b-0 rounded-2xl lg:rounded-xl p-4 cursor-pointer hover:bg-slate-700/90 lg:hover:shadow-lg transition-all"
              onClick={() => loadKpiDailyBreakdown('transactions', 'Scontrino Medio', 'blue')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-400 lg:text-gray-600 mb-1">Scontrino Medio</p>
                  <p className="text-xl lg:text-2xl font-bold text-blue-400 lg:text-blue-600">
                    €{kpis?.average_receipt?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                </div>
                <div className="bg-blue-500/20 lg:bg-blue-100 p-2 lg:p-3 rounded-xl lg:rounded-full">
                  <Receipt className="h-5 w-5 lg:h-6 lg:w-6 text-blue-400 lg:text-blue-600" />
                </div>
              </div>
            </div>

            <div 
              className="bg-slate-800/90 lg:bg-white border border-purple-500/30 lg:border-l-4 lg:border-l-purple-500 lg:border-t-0 lg:border-r-0 lg:border-b-0 rounded-2xl lg:rounded-xl p-4 cursor-pointer hover:bg-slate-700/90 lg:hover:shadow-lg transition-all"
              onClick={() => loadKpiDailyBreakdown('transactions', 'Transazioni', 'purple')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-400 lg:text-gray-600 mb-1">Transazioni</p>
                  <p className="text-xl lg:text-2xl font-bold text-purple-400 lg:text-purple-600">
                    {kpis?.total_transactions?.toLocaleString('it-IT') || 0}
                  </p>
                  {kpis?.days_with_data > 1 && (
                    <p className="text-[10px] lg:text-xs text-slate-500 lg:text-gray-500 mt-1">
                      Media: {kpis?.avg_daily_transactions?.toFixed(0)}/g
                    </p>
                  )}
                </div>
                <div className="bg-purple-500/20 lg:bg-purple-100 p-2 lg:p-3 rounded-xl lg:rounded-full">
                  <ShoppingCart className="h-5 w-5 lg:h-6 lg:w-6 text-purple-400 lg:text-purple-600" />
                </div>
              </div>
            </div>

            <div 
              className="bg-slate-800/90 lg:bg-white border border-orange-500/30 lg:border-l-4 lg:border-l-orange-500 lg:border-t-0 lg:border-r-0 lg:border-b-0 rounded-2xl lg:rounded-xl p-4 cursor-pointer hover:bg-slate-700/90 lg:hover:shadow-lg transition-all"
              onClick={() => loadKpiDailyBreakdown('items', 'Articoli Venduti', 'orange')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-400 lg:text-gray-600 mb-1">Articoli Venduti</p>
                  <p className="text-xl lg:text-2xl font-bold text-orange-400 lg:text-orange-600">
                    {kpis?.total_items_sold?.toLocaleString('it-IT') || 0}
                  </p>
                </div>
                <div className="bg-orange-500/20 lg:bg-orange-100 p-2 lg:p-3 rounded-xl lg:rounded-full">
                  <ShoppingBag className="h-5 w-5 lg:h-6 lg:w-6 text-orange-400 lg:text-orange-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Card Breakdown Finanziario - Contanti, POS, Spese - Mobile Optimized */}
          {financialBreakdown && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {/* Card Contanti */}
              <div 
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-400 p-4 sm:p-5 cursor-pointer transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl ${expandedFinancial.contanti ? 'ring-4 ring-amber-300 ring-opacity-50' : ''}`}
                onClick={() => setExpandedFinancial(prev => ({ ...prev, contanti: !prev.contanti }))}
              >
                {/* Background decoration */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <Coins className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-xs font-medium text-white/80 bg-white/20 px-2 py-1 rounded-full">
                      {expandedFinancial.contanti ? '▼' : '▶'}
                    </span>
                  </div>
                  <p className="text-white/90 text-sm font-medium mb-1">Contanti</p>
                  <p className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                    €{financialBreakdown.contanti?.totale?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                  
                  {expandedFinancial.contanti && financialBreakdown.contanti?.dettagli?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/20 space-y-2 animate-in slide-in-from-top duration-200">
                      {financialBreakdown.contanti.dettagli.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white/10 rounded-lg px-3 py-2">
                          <span className="text-white/90 text-sm">{item.descrizione}</span>
                          <span className="text-white font-semibold">€{item.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Card POS Suddivisi */}
              <div 
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-4 sm:p-5 cursor-pointer transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl ${expandedFinancial.pos ? 'ring-4 ring-emerald-300 ring-opacity-50' : ''}`}
                onClick={() => setExpandedFinancial(prev => ({ ...prev, pos: !prev.pos }))}
              >
                {/* Background decoration */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-xs font-medium text-white/80 bg-white/20 px-2 py-1 rounded-full">
                      {financialBreakdown.pos?.dettagli?.length || 0} tipi
                    </span>
                  </div>
                  <p className="text-white/90 text-sm font-medium mb-1">POS Totale</p>
                  <p className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                    €{financialBreakdown.pos?.totale?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                  
                  {expandedFinancial.pos && financialBreakdown.pos?.dettagli?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/20 space-y-2 animate-in slide-in-from-top duration-200">
                      {financialBreakdown.pos.dettagli.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white/10 rounded-lg px-3 py-2">
                          <span className="text-white/90 text-sm truncate mr-2">{item.tipo}</span>
                          <span className="text-white font-semibold whitespace-nowrap">€{item.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!expandedFinancial.pos && (
                    <p className="text-white/70 text-xs mt-2">Tocca per vedere dettagli</p>
                  )}
                </div>
              </div>

              {/* Card Spese */}
              <div 
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500 via-red-500 to-pink-500 p-4 sm:p-5 cursor-pointer transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl ${expandedFinancial.spese ? 'ring-4 ring-rose-300 ring-opacity-50' : ''}`}
                onClick={() => setExpandedFinancial(prev => ({ ...prev, spese: !prev.spese }))}
              >
                {/* Background decoration */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <Receipt className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-xs font-medium text-white/80 bg-white/20 px-2 py-1 rounded-full">
                      {financialBreakdown.spese?.dettagli?.length || 0} voci
                    </span>
                  </div>
                  <p className="text-white/90 text-sm font-medium mb-1">Spese</p>
                  <p className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                    €{financialBreakdown.spese?.totale?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                  
                  {expandedFinancial.spese && financialBreakdown.spese?.dettagli?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/20 space-y-2 animate-in slide-in-from-top duration-200">
                      {financialBreakdown.spese.dettagli.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white/10 rounded-lg px-3 py-2">
                          <span className="text-white/90 text-sm truncate mr-2">{item.descrizione}</span>
                          <span className="text-white font-semibold whitespace-nowrap">€{item.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!expandedFinancial.spese && financialBreakdown.spese?.dettagli?.length > 0 && (
                    <p className="text-white/70 text-xs mt-2">Tocca per vedere dettagli</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Articoli del Menu Meno Venduti - Per valutare cosa rimuovere */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-red-600 rotate-180" />
                  Piatti del Menù Meno Venduti
                </CardTitle>
                <CardDescription>
                  Articoli dal menu con poche o zero vendite - Valuta se rimuoverli
                </CardDescription>
              </CardHeader>
              <CardContent>
                {bottomItems.length > 0 ? (
                  <div className="space-y-3">
                    {bottomItems.map((item, index) => (
                      <div 
                        key={index} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                          item.quantita_venduta === 0 
                            ? 'bg-red-100 border border-red-300' 
                            : 'bg-red-50 hover:bg-red-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                            item.quantita_venduta === 0 ? 'bg-red-600' : 'bg-red-400'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{item.nome_menu}</p>
                            <p className="text-xs text-gray-500">
                              {item.categoria} • Prezzo menù: €{item.prezzo_menu?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {item.quantita_venduta === 0 ? (
                            <p className="font-bold text-red-600">MAI VENDUTO</p>
                          ) : (
                            <>
                              <p className="font-bold text-red-600">{item.quantita_venduta} pz</p>
                              <p className="text-sm text-gray-600">€{item.importo_venduto?.toFixed(2)}</p>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingBag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>Carica un menu per vedere i piatti meno venduti</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Analisi Categorie - Cliccabili */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5 text-purple-600" />
                      Analisi per Categoria
                    </CardTitle>
                    <CardDescription>
                      Clicca su una categoria per vedere gli articoli - {kpis?.period_label || ''}
                    </CardDescription>
                  </div>
                  <button
                    onClick={classifyProductsWithAI}
                    disabled={classifyingProducts}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {classifyingProducts ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Classificando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Classifica con AI
                      </>
                    )}
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {categories && Object.keys(categories).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(categories).map(([name, data]) => (
                      <div 
                        key={name} 
                        onClick={() => loadCategoryDetail(name)}
                        className="space-y-1 p-3 rounded-lg hover:bg-purple-50 cursor-pointer transition-all group"
                      >
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-gray-700 group-hover:text-purple-700 flex items-center gap-2">
                            {name}
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                          </span>
                          <span className="text-gray-600">€{data.importo?.toFixed(2)} ({data.percentuale}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                            style={{ width: `${data.percentuale}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500">{data.quantita} pezzi • {data.items_count} articoli diversi</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <PieChart className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>Nessun dato disponibile</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Trend Ultimi 7 Giorni */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-green-600" />
                Trend Ultimi 7 Giorni
              </CardTitle>
              <CardDescription>
                Andamento giornaliero delle vendite
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trend.length > 0 ? (
                <div className="space-y-2">
                  {trend.map((day, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="w-24 text-sm font-medium text-gray-700">
                        {new Date(day.date).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">€{day.revenue?.toFixed(2)}</span>
                          <span className="text-xs text-gray-500">{day.transactions} transazioni</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full"
                            style={{ width: `${(day.revenue / Math.max(...trend.map(d => d.revenue || 1)) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-700">€{day.avg_receipt?.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">media</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Nessun dato disponibile</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Classification: Top Piatti e Vini */}
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Classificazione AI: Piatti e Vini Più Venduti
              </CardTitle>
              <CardDescription>
                Clicca su un prodotto per vedere il dettaglio delle vendite giornaliere
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filtri Periodo AI */}
              <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-white rounded-lg border">
                <span className="font-medium text-gray-700 text-sm">Periodo:</span>
                
                <div className="flex bg-purple-100 rounded-lg p-1">
                  <button
                    onClick={() => setAiPeriodType('day')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      aiPeriodType === 'day' 
                        ? 'bg-purple-600 text-white shadow' 
                        : 'text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    Giorno
                  </button>
                  <button
                    onClick={() => setAiPeriodType('month')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      aiPeriodType === 'month' 
                        ? 'bg-purple-600 text-white shadow' 
                        : 'text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    Mese
                  </button>
                  <button
                    onClick={() => setAiPeriodType('year')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      aiPeriodType === 'year' 
                        ? 'bg-purple-600 text-white shadow' 
                        : 'text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    Anno
                  </button>
                </div>

                <select
                  value={aiSelectedYear}
                  onChange={(e) => setAiSelectedYear(parseInt(e.target.value))}
                  className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                >
                  {availableYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>

                {(aiPeriodType === 'month' || aiPeriodType === 'day') && (
                  <select
                    value={aiSelectedMonth}
                    onChange={(e) => setAiSelectedMonth(parseInt(e.target.value))}
                    className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                  >
                    {MONTH_NAMES.slice(1).map((name, index) => (
                      <option key={index + 1} value={index + 1}>{name}</option>
                    ))}
                  </select>
                )}

                {aiPeriodType === 'day' && (
                  <select
                    value={aiSelectedDay}
                    onChange={(e) => setAiSelectedDay(parseInt(e.target.value))}
                    className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                  >
                    {Array.from({ length: getDaysInMonth(aiSelectedYear, aiSelectedMonth) }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                )}

                <div className="ml-auto text-sm text-purple-600 font-medium">
                  {aiPeriodType === 'day' && `${aiSelectedDay} ${MONTH_NAMES[aiSelectedMonth]} ${aiSelectedYear}`}
                  {aiPeriodType === 'month' && `${MONTH_NAMES[aiSelectedMonth]} ${aiSelectedYear}`}
                  {aiPeriodType === 'year' && `Anno ${aiSelectedYear}`}
                </div>
              </div>

              {aiClassificationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
                  <span className="text-gray-600">Analisi AI in corso...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top 10 Piatti */}
                  <div>
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-orange-700 mb-4">
                      <UtensilsCrossed className="h-5 w-5" />
                      🍝 Top 10 Piatti di Cibo
                    </h3>
                    {topPiatti.length > 0 ? (
                      <div className="space-y-2">
                        {topPiatti.map((item, index) => (
                          <div 
                            key={index} 
                            onClick={() => loadProductDetail(item.codice, item.descrizione)}
                            className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm hover:shadow-md hover:bg-orange-50 transition-all cursor-pointer group"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                                index === 0 ? 'bg-yellow-500' : 
                                index === 1 ? 'bg-gray-400' : 
                                index === 2 ? 'bg-orange-600' : 'bg-orange-300'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 text-sm group-hover:text-orange-700">{item.descrizione}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <p className="font-bold text-orange-600">{item.quantita} pz</p>
                                <p className="text-xs text-gray-500">€{item.importo?.toFixed(2)}</p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-orange-600" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <UtensilsCrossed className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Nessun piatto trovato</p>
                      </div>
                    )}
                  </div>

                  {/* Top 10 Vini */}
                  <div>
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-red-700 mb-4">
                      <Wine className="h-5 w-5" />
                      Top 10 Bottiglie di Vino
                    </h3>
                    {topVini.length > 0 ? (
                      <div className="space-y-2">
                        {topVini.map((item, index) => (
                          <div 
                            key={index} 
                            onClick={() => loadProductDetail(item.codice, item.descrizione)}
                            className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm hover:shadow-md hover:bg-red-50 transition-all cursor-pointer group"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                                index === 0 ? 'bg-yellow-500' : 
                                index === 1 ? 'bg-gray-400' : 
                                index === 2 ? 'bg-red-600' : 'bg-red-300'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 text-sm group-hover:text-red-700">{item.descrizione}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <p className="font-bold text-red-600">{item.quantita} pz</p>
                                <p className="text-xs text-gray-500">€{item.importo?.toFixed(2)}</p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-red-600" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <Wine className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Nessun vino trovato</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal Dettaglio Prodotto */}
          {selectedProduct && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <div>
                    <h3 className="font-bold text-lg">{selectedProduct.descrizione}</h3>
                    <p className="text-purple-100 text-sm">Codice: {selectedProduct.codice}</p>
                  </div>
                  <button 
                    onClick={closeProductDetail}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="p-4 overflow-y-auto max-h-[60vh]">
                  {productDetailLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
                      <span className="text-gray-600">Caricamento dettagli...</span>
                    </div>
                  ) : productDetail ? (
                    <>
                      {/* Riepilogo */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-600 font-medium">Giorni Vendita</p>
                          <p className="text-xl font-bold text-purple-700">{productDetail.giorni_vendita}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-green-600 font-medium">Totale Pezzi</p>
                          <p className="text-xl font-bold text-green-700">{productDetail.totale_quantita}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600 font-medium">Totale</p>
                          <p className="text-xl font-bold text-blue-700">€{productDetail.totale_importo?.toFixed(2)}</p>
                        </div>
                      </div>

                      {/* Lista Vendite per Data */}
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Dettaglio Vendite per Data
                      </h4>
                      
                      {productDetail.vendite?.length > 0 ? (
                        <div className="space-y-2">
                          {productDetail.vendite.map((vendita, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                  <Calendar className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {new Date(vendita.data).toLocaleDateString('it-IT', { 
                                      weekday: 'short', 
                                      day: 'numeric', 
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-purple-600">{vendita.quantita} pz</p>
                                <p className="text-sm text-gray-500">€{vendita.importo?.toFixed(2)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <p>Nessuna vendita trovata nel periodo selezionato</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Impossibile caricare i dettagli</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal Dettaglio KPI Giornaliero */}
          {selectedKpi && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                <div className={`flex items-center justify-between p-4 border-b bg-gradient-to-r ${
                  selectedKpi.color === 'green' ? 'from-green-600 to-green-500' :
                  selectedKpi.color === 'blue' ? 'from-blue-600 to-blue-500' :
                  selectedKpi.color === 'purple' ? 'from-purple-600 to-purple-500' :
                  'from-orange-600 to-orange-500'
                } text-white`}>
                  <div>
                    <h3 className="font-bold text-lg">{selectedKpi.label}</h3>
                    <p className="text-sm opacity-90">Dettaglio giornaliero - {kpis?.period_label}</p>
                  </div>
                  <button 
                    onClick={closeKpiDetail}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="p-4 overflow-y-auto max-h-[70vh]">
                  {kpiDetailLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
                        selectedKpi.color === 'green' ? 'border-green-600' :
                        selectedKpi.color === 'blue' ? 'border-blue-600' :
                        selectedKpi.color === 'purple' ? 'border-purple-600' :
                        'border-orange-600'
                      } mr-3`}></div>
                      <span className="text-gray-600">Caricamento dettagli...</span>
                    </div>
                  ) : kpiDailyData ? (
                    <>
                      {/* Riepilogo */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className={`rounded-lg p-3 text-center ${
                          selectedKpi.color === 'green' ? 'bg-green-50' :
                          selectedKpi.color === 'blue' ? 'bg-blue-50' :
                          selectedKpi.color === 'purple' ? 'bg-purple-50' :
                          'bg-orange-50'
                        }`}>
                          <p className={`text-xs font-medium ${
                            selectedKpi.color === 'green' ? 'text-green-600' :
                            selectedKpi.color === 'blue' ? 'text-blue-600' :
                            selectedKpi.color === 'purple' ? 'text-purple-600' :
                            'text-orange-600'
                          }`}>Totale Periodo</p>
                          <p className={`text-xl font-bold ${
                            selectedKpi.color === 'green' ? 'text-green-700' :
                            selectedKpi.color === 'blue' ? 'text-blue-700' :
                            selectedKpi.color === 'purple' ? 'text-purple-700' :
                            'text-orange-700'
                          }`}>
                            {selectedKpi.metric === 'revenue' || selectedKpi.metric === 'nero' || 
                             selectedKpi.metric === 'fiscale' || selectedKpi.metric === 'fatture' 
                              ? `€${kpiDailyData.total?.toLocaleString('it-IT', {minimumFractionDigits: 2})}` 
                              : kpiDailyData.total?.toLocaleString('it-IT')}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-600 font-medium">Giorni con Dati</p>
                          <p className="text-xl font-bold text-gray-700">{kpiDailyData.days_count}</p>
                        </div>
                      </div>

                      {/* Lista Dati Giornalieri */}
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Dettaglio per Giorno
                      </h4>
                      
                      {kpiDailyData.daily_data?.length > 0 ? (
                        <div className="space-y-2">
                          {kpiDailyData.daily_data.map((day, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  selectedKpi.color === 'green' ? 'bg-green-100' :
                                  selectedKpi.color === 'blue' ? 'bg-blue-100' :
                                  selectedKpi.color === 'purple' ? 'bg-purple-100' :
                                  'bg-orange-100'
                                }`}>
                                  <Calendar className={`h-5 w-5 ${
                                    selectedKpi.color === 'green' ? 'text-green-600' :
                                    selectedKpi.color === 'blue' ? 'text-blue-600' :
                                    selectedKpi.color === 'purple' ? 'text-purple-600' :
                                    'text-orange-600'
                                  }`} />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {new Date(day.data).toLocaleDateString('it-IT', { 
                                      weekday: 'short', 
                                      day: 'numeric', 
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {day.coperti} transazioni • {day.items} articoli
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${
                                  selectedKpi.color === 'green' ? 'text-green-600' :
                                  selectedKpi.color === 'blue' ? 'text-blue-600' :
                                  selectedKpi.color === 'purple' ? 'text-purple-600' :
                                  'text-orange-600'
                                }`}>
                                  {selectedKpi.metric === 'revenue' || selectedKpi.metric === 'nero' || 
                                   selectedKpi.metric === 'fiscale' || selectedKpi.metric === 'fatture'
                                    ? `€${day.valore?.toLocaleString('it-IT', {minimumFractionDigits: 2})}` 
                                    : day.valore?.toLocaleString('it-IT')}
                                </p>
                                <p className="text-xs text-gray-500">
                                  €{day.revenue?.toLocaleString('it-IT', {minimumFractionDigits: 2})} totale
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <p>Nessun dato trovato nel periodo selezionato</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Impossibile caricare i dettagli</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal Dettaglio Categoria */}
          {selectedCategory && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <div>
                    <h3 className="font-bold text-lg">{selectedCategory}</h3>
                    <p className="text-sm opacity-90">Articoli venduti in questa categoria - {kpis?.period_label}</p>
                  </div>
                  <button 
                    onClick={closeCategoryDetail}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="p-4 overflow-y-auto max-h-[70vh]">
                  {categoryDetailLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
                      <span className="text-gray-600">Caricamento articoli...</span>
                    </div>
                  ) : categoryDetail ? (
                    <>
                      {/* Riepilogo */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-600 font-medium">Articoli Diversi</p>
                          <p className="text-xl font-bold text-purple-700">{categoryDetail.articoli_diversi}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-green-600 font-medium">Totale Pezzi</p>
                          <p className="text-xl font-bold text-green-700">{categoryDetail.totale_quantita}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600 font-medium">Totale Incasso</p>
                          <p className="text-xl font-bold text-blue-700">€{categoryDetail.totale_importo?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                        </div>
                      </div>

                      {/* Lista Articoli */}
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <ShoppingBag className="h-4 w-4" />
                        Articoli nella Categoria
                      </h4>
                      
                      {categoryDetail.items?.length > 0 ? (
                        <div className="space-y-2">
                          {categoryDetail.items.map((item, index) => (
                            <div 
                              key={index} 
                              onClick={() => { closeCategoryDetail(); loadProductDetail(item.codice, item.descrizione); }}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer group"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                                  index === 0 ? 'bg-yellow-500' : 
                                  index === 1 ? 'bg-gray-400' : 
                                  index === 2 ? 'bg-purple-600' : 'bg-purple-300'
                                }`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 group-hover:text-purple-700">{item.descrizione}</p>
                                  <p className="text-xs text-gray-500">Cod: {item.codice} • {item.giorni_vendita} giorni vendita</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <p className="font-bold text-purple-600">{item.quantita} pz</p>
                                  <p className="text-sm text-gray-500">€{item.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                                </div>
                                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <p>Nessun articolo trovato in questa categoria</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Impossibile caricare i dettagli</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
